import React, { useState, useCallback } from 'react';
import {
    View,
    Text,
    StyleSheet,
    ScrollView,
    TouchableOpacity,
} from 'react-native';
import { FlashList } from '@shopify/flash-list';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useFocusEffect } from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { StatCard } from '../components/StatCard';
import { CustomButton } from '../components/CustomButton';
import { AppModal, ModalType } from '../components/AppModal';
import { LoadingOverlay } from '../components/LoadingOverlay';
import ApiService from '../services/ApiService';
import { Cow, CowRecord } from '../models/types';
import {
    formatCurrency,
    formatNumber,
    formatDate,
    getRecordTypeIcon,
    getCowStatusColor,
} from '../utils/helpers';
import { useAuth } from '../contexts/AuthContext';
import DetailScreenLoading from '../components/DetailScreenLoading';
import { ScreenHeader } from '../components/ScreenHeader';

const CowDetailScreen = ({ navigation, route }: any) => {
    const cow = route.params?.cow as Cow;
    const { user } = useAuth();
    const [records, setRecords] = useState<{
        fodder: CowRecord[];
        medicine: CowRecord[];
        milk: CowRecord[];
    }>({
        fodder: [],
        medicine: [],
        milk: [],
    });
    const [selectedTab, setSelectedTab] = useState<'fodder' | 'medicine' | 'milk'>(
        'milk',
    );
    const [isBasicInfoExpanded, setIsBasicInfoExpanded] = useState(false);
    const [modal, setModal] = useState<{
        visible: boolean;
        type: ModalType;
        title: string;
        message: string;
        onConfirm?: () => void;
        confirmText?: string;
    }>({ visible: false, type: 'info', title: '', message: '' });
    const [statusModal, setStatusModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);

    const loadRecords = async () => {
        try {
            const fodderRecords = await ApiService.getCowRecords(cow.id, 'fodder');
            const medicineRecords = await ApiService.getCowRecords(
                cow.id,
                'medicine',
            );
            const milkRecords = await ApiService.getCowRecords(cow.id, 'milk');

            setRecords({
                fodder: fodderRecords,
                medicine: medicineRecords,
                milk: milkRecords,
            });
        } catch (error) {
            console.error('Error loading records:', error);
        } finally {
            setInitialLoading(false);
        }
    };

    useFocusEffect(
        useCallback(() => {
            loadRecords();
        }, [cow.id]),
    );

    const handleDeleteCow = () => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Cow',
            message: 'Are you sure you want to delete this cow? This action cannot be undone.',
            confirmText: 'Delete',
            onConfirm: async () => {
                setModal(prev => ({ ...prev, visible: false }));
                setLoading(true);
                try {
                    await ApiService.deleteCow(cow.id);
                    navigation.goBack();
                } catch (error) {
                    console.error('Error deleting cow:', error);
                    setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to delete cow' });
                    setLoading(false);
                }
            },
        });
    };

    const handleChangeStatus = () => {
        setStatusModal(true);
    };

    const updateCowStatus = async (newStatus: 'active' | 'sold' | 'deceased') => {
        setStatusModal(false);
        setLoading(true);
        try {
            const now = new Date().toISOString();
            const updateData = {
                currentStatus: newStatus,
                isPregnant: newStatus === 'active' ? cow.isPregnant : false,
                pregnancyDate: newStatus === 'active' ? cow.pregnancyDate : undefined,
                expectedCalvingDate: newStatus === 'active' ? cow.expectedCalvingDate : undefined,
                updatedAt: now,
            };
            await ApiService.updateCow(cow.id, updateData);
            setLoading(false);
            setModal({
                visible: true,
                type: 'success',
                title: 'Success',
                message: `Cow has been marked as ${newStatus}.`,
                onConfirm: () => {
                    setModal(prev => ({ ...prev, visible: false }));
                    navigation.goBack();
                },
            });
        } catch (error) {
            console.error('Error updating cow status:', error);
            setLoading(false);
            setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to update cow status' });
        }
    };

    const calculateTotals = () => {
        const totalMilk = records.milk.reduce(
            (acc, r) => acc + (r.milkQuantity || 0),
            0,
        );
        const totalFodderCost = records.fodder.reduce(
            (acc, r) => acc + (r.fodderCost || 0),
            0,
        );
        const totalMedicineCost = records.medicine.reduce(
            (acc, r) => acc + (r.medicineCost || 0),
            0,
        );
        const totalMilkRevenue = records.milk.reduce(
            (acc, r) => acc + (r.milkTotal || 0),
            0,
        );

        return { totalMilk, totalFodderCost, totalMedicineCost, totalMilkRevenue };
    };

    const { totalMilk, totalFodderCost, totalMedicineCost, totalMilkRevenue } = calculateTotals();

    const handleDeleteCowRecord = (record: CowRecord) => {
        setModal({
            visible: true,
            type: 'confirm',
            title: 'Delete Record',
            message: 'Are you sure you want to delete this record?',
            confirmText: 'Delete',
            onConfirm: async () => {
                setModal(prev => ({ ...prev, visible: false }));
                setLoading(true);
                try {
                    await ApiService.deleteCowRecord(record.id);
                    await loadRecords(); // Await this to keep loading state true
                } catch (error) {
                    console.error('Error deleting record:', error);
                    setModal({ visible: true, type: 'error', title: 'Error', message: 'Failed to delete record' });
                } finally {
                    setLoading(false);
                }
            },
        });
    };

    const handleEditCowRecord = (record: CowRecord) => {
        navigation.navigate('AddCowRecord', { cow, existingRecord: record });
    };

    const renderRecordItem = ({ item }: { item: CowRecord }) => {
        const icon = getRecordTypeIcon(item.recordType);

        // Get main title based on record type
        const getTitle = () => {
            if (item.recordType === 'milk') return `${formatNumber(item.milkQuantity || 0)} L`;
            if (item.recordType === 'fodder') return `${formatNumber(item.fodderQuantity || 0)} kg`;
            if (item.recordType === 'medicine') return item.medicineName || 'Medicine';
            return '';
        };

        // Get secondary value
        const getSecondaryValue = () => {
            if (item.recordType === 'milk') {
                if (item.milkPrice) {
                    return `₹${item.milkPrice}/L | ₹${formatNumber(item.milkTotal || 0)}`;
                }
                return item.milkTime || '';
            }
            if (item.recordType === 'fodder') return formatCurrency(item.fodderCost || 0);
            if (item.recordType === 'medicine') return formatCurrency(item.medicineCost || 0);
            return '';
        };

        return (
            <TouchableOpacity
                style={[styles.recordCard, { borderLeftColor: icon.color }]}
                onPress={() => handleEditCowRecord(item)}
                activeOpacity={0.7}
            >
                {/* Left: Icon */}
                <View style={[styles.recordIcon, { backgroundColor: icon.color + '18' }]}>
                    <Icon name={icon.name} size={24} color={icon.color} />
                </View>

                {/* Center: Content */}
                <View style={styles.recordContent}>
                    {/* Main Title */}
                    <Text style={styles.recordTitle} numberOfLines={1}>
                        {getTitle()}
                    </Text>

                    {/* Date */}
                    <Text style={styles.recordDate}>
                        {formatDate(item.date)}
                    </Text>

                    {/* Notes if present */}
                    {item.notes && (
                        <Text style={styles.recordNotes} numberOfLines={1}>
                            {item.notes}
                        </Text>
                    )}
                </View>

                {/* Right: Value & Delete */}
                <View style={styles.recordRightSection}>
                    <Text style={[styles.recordValue, { color: icon.color }]}>
                        {getSecondaryValue()}
                    </Text>
                    <TouchableOpacity
                        style={styles.deleteButton}
                        onPress={() => handleDeleteCowRecord(item)}
                        hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
                    >
                        <Icon name="trash-can-outline" size={18} color={theme.colors.error} />
                    </TouchableOpacity>
                </View>
            </TouchableOpacity>
        );
    };

    const renderEmptyRecords = () => (
        <View style={styles.emptyContainer}>
            <Icon
                name={getRecordTypeIcon(selectedTab).name}
                size={60}
                color={theme.colors.textLight}
            />
            <Text style={styles.emptyText}>No {selectedTab} records yet</Text>
        </View>
    );

    const statusColor = getCowStatusColor(cow.currentStatus);

    if (initialLoading) {
        return (
            <SafeAreaView style={styles.container}>
                <ScreenHeader
                    title="Cow Details"
                    showBackButton={true}
                    navigation={navigation}
                />
                <DetailScreenLoading showMessage={false} />
            </SafeAreaView>
        );
    }

    return (
        <SafeAreaView style={styles.container}>
            <ScreenHeader
                title="Cow Details"
                showBackButton={true}
                navigation={navigation}
            />
            <ScrollView showsVerticalScrollIndicator={false}>
                {/* Cow Header Card */}
                <View style={styles.headerCard}>
                    <View style={styles.headerTop}>
                        <View style={styles.cowIconContainer}>
                            <Icon name="cow" size={36} color="#FFFFFF" />
                        </View>
                        <View style={styles.cowHeaderInfo}>
                            <Text style={styles.cowId}>{cow.id}</Text>
                            {cow.name && <Text style={styles.cowName}>{cow.name}</Text>}
                        </View>
                        <TouchableOpacity
                            style={[styles.statusBadge, { backgroundColor: statusColor + '20', borderColor: statusColor }]}
                            onPress={handleChangeStatus}
                            activeOpacity={0.7}
                        >
                            <Text style={[styles.statusText, { color: statusColor }]}>
                                {cow.currentStatus.toUpperCase()}
                            </Text>
                            <Icon name="chevron-down" size={14} color={statusColor} />
                        </TouchableOpacity>
                    </View>

                    {/* Action Icons Row */}
                    <View style={styles.headerActions}>
                        <TouchableOpacity
                            style={styles.headerActionBtn}
                            onPress={() => navigation.navigate('AddEditCow', { cow })}
                            activeOpacity={0.7}
                        >
                            <Icon name="pencil-outline" size={18} color={theme.colors.primary} />
                            <Text style={styles.headerActionText}>Edit</Text>
                        </TouchableOpacity>
                        <View style={styles.headerActionDivider} />
                        <TouchableOpacity
                            style={styles.headerActionBtn}
                            onPress={handleDeleteCow}
                            activeOpacity={0.7}
                        >
                            <Icon name="trash-can-outline" size={18} color={theme.colors.error} />
                            <Text style={[styles.headerActionText, { color: theme.colors.error }]}>Delete</Text>
                        </TouchableOpacity>
                    </View>
                </View>

                {/* Info Card - Collapsible */}
                <View style={styles.infoCard}>
                    <TouchableOpacity
                        style={styles.infoCardHeader}
                        onPress={() => setIsBasicInfoExpanded(!isBasicInfoExpanded)}
                        activeOpacity={0.7}
                    >
                        <Text style={styles.cardTitle}>Basic Information</Text>
                        <Icon
                            name={isBasicInfoExpanded ? 'chevron-up' : 'chevron-down'}
                            size={24}
                            color={theme.colors.textSecondary}
                        />
                    </TouchableOpacity>

                    {isBasicInfoExpanded && (
                        <>
                            <View style={styles.infoGrid}>
                                {cow.breed && (
                                    <View style={styles.infoItem}>
                                        <View style={styles.infoIconContainer}>
                                            <Icon name="tag" size={18} color={theme.colors.primary} />
                                        </View>
                                        <View style={styles.infoContent}>
                                            <Text style={styles.infoLabel}>Breed</Text>
                                            <Text style={styles.infoValue}>{cow.breed}</Text>
                                        </View>
                                    </View>
                                )}
                                {cow.dateOfBirth && (
                                    <View style={styles.infoItem}>
                                        <View style={styles.infoIconContainer}>
                                            <Icon name="cake-variant" size={18} color={theme.colors.primary} />
                                        </View>
                                        <View style={styles.infoContent}>
                                            <Text style={styles.infoLabel}>Date of Birth</Text>
                                            <Text style={styles.infoValue}>
                                                {formatDate(cow.dateOfBirth)}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                                {cow.purchaseDate && (
                                    <View style={styles.infoItem}>
                                        <View style={styles.infoIconContainer}>
                                            <Icon name="calendar" size={18} color={theme.colors.primary} />
                                        </View>
                                        <View style={styles.infoContent}>
                                            <Text style={styles.infoLabel}>Purchase Date</Text>
                                            <Text style={styles.infoValue}>
                                                {formatDate(cow.purchaseDate)}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                                {cow.purchasePrice && (
                                    <View style={styles.infoItem}>
                                        <View style={styles.infoIconContainer}>
                                            <Icon name="currency-inr" size={18} color={theme.colors.primary} />
                                        </View>
                                        <View style={styles.infoContent}>
                                            <Text style={styles.infoLabel}>Purchase Price</Text>
                                            <Text style={styles.infoValue}>
                                                {formatCurrency(cow.purchasePrice)}
                                            </Text>
                                        </View>
                                    </View>
                                )}
                            </View>

                            {cow.notes && (
                                <View style={styles.notesContainer}>
                                    <View style={styles.notesHeader}>
                                        <Icon name="note-text" size={18} color={theme.colors.primary} />
                                        <Text style={styles.notesLabel}>Notes</Text>
                                    </View>
                                    <Text style={styles.notesText}>{cow.notes}</Text>
                                </View>
                            )}
                        </>
                    )}
                </View>

                {/* Expenses Summary - List Style */}
                <View style={styles.expensesSummaryCard}>
                    <Text style={styles.expensesSummaryTitle}>Expenses Summary</Text>

                    {/* Fodder Cost Row */}
                    <View style={styles.expenseRow}>
                        <View style={[styles.expenseIconBg, { backgroundColor: '#E8F5E9' }]}>
                            <Icon name="grass" size={20} color="#4CAF50" />
                        </View>
                        <Text style={styles.expenseLabel}>Fodder Cost</Text>
                        <Text style={styles.expenseValue}>{formatCurrency(totalFodderCost)}</Text>
                    </View>

                    <View style={styles.expenseDivider} />

                    {/* Medicine Cost Row */}
                    <View style={styles.expenseRow}>
                        <View style={[styles.expenseIconBg, { backgroundColor: '#FCE4EC' }]}>
                            <Icon name="medical-bag" size={20} color="#E91E63" />
                        </View>
                        <Text style={styles.expenseLabel}>Medicine Cost</Text>
                        <Text style={styles.expenseValue}>{formatCurrency(totalMedicineCost)}</Text>
                    </View>

                    <View style={styles.expenseDivider} />

                    {/* Milk Production Row */}
                    <View style={styles.expenseRow}>
                        <View style={[styles.expenseIconBg, { backgroundColor: '#E3F2FD' }]}>
                            <Icon name="cup" size={20} color="#2196F3" />
                        </View>
                        <Text style={styles.expenseLabel}>Milk Production</Text>
                        <Text style={styles.expenseValue}>{formatNumber(totalMilk)} L</Text>
                    </View>

                    <View style={styles.expenseDivider} />

                    {/* Milk Revenue Row - only shown if any milk record has a price */}
                    {totalMilkRevenue > 0 && (
                        <>
                            <View style={styles.expenseRow}>
                                <View style={[styles.expenseIconBg, { backgroundColor: '#E8F5E9' }]}>
                                    <Icon name="cash" size={20} color="#388E3C" />
                                </View>
                                <Text style={styles.expenseLabel}>Milk Revenue</Text>
                                <Text style={[styles.expenseValue, { color: '#388E3C' }]}>
                                    {formatCurrency(totalMilkRevenue)}
                                </Text>
                            </View>
                            <View style={styles.expenseDivider} />
                        </>
                    )}

                    {/* Total Expenses Row */}
                    <View style={styles.expenseRow}>
                        <View style={[styles.expenseIconBg, { backgroundColor: '#FFEBEE' }]}>
                            <Icon name="wallet-outline" size={20} color="#E53935" />
                        </View>
                        <Text style={styles.expenseLabel}>Total Expenses</Text>
                        <Text style={[styles.expenseValue, { color: '#E53935', fontWeight: '700' }]}>
                            {formatCurrency(totalFodderCost + totalMedicineCost)}
                        </Text>
                    </View>
                </View>

                <View style={styles.recordsSection}>
                    <View style={styles.recordsHeader}>
                        <Text style={styles.sectionTitle}>Records</Text>
                        {cow.currentStatus === 'active' ? (
                            <TouchableOpacity
                                style={styles.addRecordButton}
                                onPress={() =>
                                    navigation.navigate('AddCowRecord', { cow, recordType: selectedTab })
                                }>
                                <Icon name="plus" size={20} color={theme.colors.primary} />
                                <Text style={styles.addRecordText}>Add Record</Text>
                            </TouchableOpacity>
                        ) : (
                            <View style={styles.inactiveMessage}>
                                <Icon name="lock" size={16} color={theme.colors.textLight} />
                                <Text style={styles.inactiveText}>
                                    {cow.currentStatus === 'sold' ? 'Sold' : 'Deceased'}
                                </Text>
                            </View>
                        )}
                    </View>

                    <View style={styles.tabs}>
                        {['milk', 'fodder', 'medicine'].map(tab => {
                            const icon = getRecordTypeIcon(tab);
                            const isActive = selectedTab === tab;
                            return (
                                <TouchableOpacity
                                    key={tab}
                                    style={[styles.tab, isActive && styles.tab_active]}
                                    onPress={() =>
                                        setSelectedTab(tab as 'fodder' | 'medicine' | 'milk')
                                    }>
                                    <Icon
                                        name={icon.name}
                                        size={20}
                                        color={isActive ? icon.color : theme.colors.textSecondary}
                                    />
                                    <Text
                                        style={[
                                            styles.tabText,
                                            isActive && { color: icon.color },
                                        ]}>
                                        {tab.charAt(0).toUpperCase() + tab.slice(1)}
                                    </Text>
                                </TouchableOpacity>
                            );
                        })}
                    </View>

                    <FlashList
                        data={records[selectedTab]}
                        renderItem={renderRecordItem}
                        keyExtractor={item => item.id}
                        ListEmptyComponent={renderEmptyRecords}
                        scrollEnabled={false}
                    />
                </View>

                <View style={styles.bottomPadding} />
            </ScrollView>

            {/* Status Change Modal */}
            <AppModal
                visible={statusModal}
                type="info"
                title="Change Cow Status"
                message={`Current status: ${cow.currentStatus.toUpperCase()}\n\nSelect a new status:`}
                onClose={() => setStatusModal(false)}
                customButtons={
                    <View style={{ gap: 8 }}>
                        {cow.currentStatus !== 'active' && (
                            <TouchableOpacity
                                style={{ backgroundColor: theme.colors.success, padding: 12, borderRadius: 8, alignItems: 'center' }}
                                onPress={() => updateCowStatus('active')}
                            >
                                <Text style={{ color: '#FFF', fontWeight: '600' }}>Mark as Active</Text>
                            </TouchableOpacity>
                        )}
                        {cow.currentStatus !== 'sold' && (
                            <TouchableOpacity
                                style={{ backgroundColor: theme.colors.warning, padding: 12, borderRadius: 8, alignItems: 'center' }}
                                onPress={() => updateCowStatus('sold')}
                            >
                                <Text style={{ color: '#FFF', fontWeight: '600' }}>Mark as Sold</Text>
                            </TouchableOpacity>
                        )}
                        {cow.currentStatus !== 'deceased' && (
                            <TouchableOpacity
                                style={{ backgroundColor: theme.colors.error, padding: 12, borderRadius: 8, alignItems: 'center' }}
                                onPress={() => updateCowStatus('deceased')}
                            >
                                <Text style={{ color: '#FFF', fontWeight: '600' }}>Mark as Deceased</Text>
                            </TouchableOpacity>
                        )}
                        <TouchableOpacity
                            style={{ backgroundColor: theme.colors.border, padding: 12, borderRadius: 8, alignItems: 'center', marginTop: 8 }}
                            onPress={() => setStatusModal(false)}
                        >
                            <Text style={{ color: theme.colors.text, fontWeight: '600' }}>Cancel</Text>
                        </TouchableOpacity>
                    </View>
                }
            />

            {/* General Modal */}
            <AppModal
                visible={modal.visible}
                type={modal.type}
                title={modal.title}
                message={modal.message}
                onClose={() => setModal(prev => ({ ...prev, visible: false }))}
                onConfirm={modal.onConfirm}
                confirmText={modal.confirmText || 'OK'}
            />
        </SafeAreaView>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: theme.colors.background,
    },
    header: {
        backgroundColor: theme.colors.surface,
        padding: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    headerCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        marginBottom: 0,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    headerTop: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerActions: {
        flexDirection: 'row',
        marginTop: theme.spacing.md,
        paddingTop: theme.spacing.md,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
        justifyContent: 'center',
    },
    headerActionBtn: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.xs,
        paddingHorizontal: theme.spacing.lg,
        gap: theme.spacing.xs,
    },
    headerActionText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
    },
    headerActionDivider: {
        width: 1,
        height: 20,
        backgroundColor: theme.colors.border,
    },
    cowIconContainer: {
        width: 56,
        height: 56,
        borderRadius: 28,
        backgroundColor: theme.colors.primary,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    cowHeaderInfo: {
        flex: 1,
    },
    cowId: {
        ...theme.typography.h4,
        color: theme.colors.text,
        fontWeight: '700',
    },
    cowName: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
    },
    statusBadge: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.round,
        borderWidth: 1,
        gap: 4,
    },
    statusText: {
        ...theme.typography.caption,
        fontWeight: '700',
        fontSize: 11,
    },
    infoCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.lg,
        ...theme.shadows.sm,
    },
    infoCardHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    cardTitle: {
        ...theme.typography.h6,
        color: theme.colors.text,
        fontWeight: '700',
    },
    infoGrid: {
        gap: theme.spacing.md,
        marginTop: theme.spacing.md,
    },
    infoItem: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    infoIconContainer: {
        width: 36,
        height: 36,
        borderRadius: 18,
        backgroundColor: theme.colors.primary + '15',
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    infoContent: {
        flex: 1,
    },
    infoLabel: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    infoValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    notesContainer: {
        marginTop: theme.spacing.lg,
        padding: theme.spacing.md,
        backgroundColor: theme.colors.background,
        borderRadius: theme.borderRadius.md,
    },
    notesHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
        marginBottom: theme.spacing.xs,
    },
    notesLabel: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginBottom: theme.spacing.xs,
        fontWeight: '600',
    },
    notesText: {
        ...theme.typography.body2,
        color: theme.colors.text,
    },
    // Expenses Summary - List Style
    expensesSummaryCard: {
        backgroundColor: theme.colors.surface,
        margin: theme.spacing.md,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        ...theme.shadows.sm,
    },
    expensesSummaryTitle: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.md,
    },
    expenseRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: theme.spacing.sm,
    },
    expenseIconBg: {
        width: 36,
        height: 36,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    expenseLabel: {
        ...theme.typography.body1,
        color: theme.colors.text,
        flex: 1,
    },
    expenseValue: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
    },
    expenseDivider: {
        height: 1,
        backgroundColor: theme.colors.border,
        marginLeft: 52,
    },
    sectionTitle: {
        ...theme.typography.h5,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: theme.spacing.md,
    },
    recordsSection: {
        padding: theme.spacing.lg,
    },
    recordsHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing.md,
    },
    addRecordButton: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: theme.spacing.md,
        paddingVertical: theme.spacing.sm,
        backgroundColor: theme.colors.primary + '20',
        borderRadius: theme.borderRadius.md,
    },
    addRecordText: {
        ...theme.typography.body2,
        color: theme.colors.primary,
        fontWeight: '600',
        marginLeft: theme.spacing.xs,
    },
    tabs: {
        flexDirection: 'row',
        marginBottom: theme.spacing.md,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.xs,
    },
    tab: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: theme.spacing.sm,
        borderRadius: theme.borderRadius.md,
    },
    tab_active: {
        backgroundColor: theme.colors.background,
    },
    tabText: {
        ...theme.typography.body2,
        color: theme.colors.textSecondary,
        marginLeft: theme.spacing.xs,
        fontWeight: '600',
    },
    recordCard: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderRadius: theme.borderRadius.lg,
        padding: theme.spacing.md,
        marginBottom: theme.spacing.sm,
        borderLeftWidth: 4,
        ...theme.shadows.sm,
    },
    recordIcon: {
        width: 44,
        height: 44,
        borderRadius: theme.borderRadius.md,
        justifyContent: 'center',
        alignItems: 'center',
        marginRight: theme.spacing.md,
    },
    recordContent: {
        flex: 1,
        paddingRight: theme.spacing.sm,
    },
    recordTitle: {
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '700',
        marginBottom: 2,
    },
    recordDate: {
        ...theme.typography.caption,
        color: theme.colors.textSecondary,
    },
    recordDetail: {
        ...theme.typography.body1,
        color: theme.colors.text,
        marginBottom: theme.spacing.xs,
    },
    recordNotes: {
        ...theme.typography.caption,
        color: theme.colors.textLight,
        fontStyle: 'italic',
        marginTop: 2,
    },
    recordRightSection: {
        alignItems: 'flex-end',
        gap: theme.spacing.xs,
    },
    recordValue: {
        ...theme.typography.body1,
        fontWeight: '600',
    },
    emptyContainer: {
        alignItems: 'center',
        paddingVertical: theme.spacing.xxl,
    },
    emptyText: {
        ...theme.typography.body1,
        color: theme.colors.textSecondary,
        marginTop: theme.spacing.md,
    },
    actionSection: {
        padding: theme.spacing.md,
        gap: theme.spacing.md,
    },
    changeStatusButton: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        ...theme.shadows.sm,
    },
    changeStatusText: {
        flex: 1,
        ...theme.typography.body1,
        color: theme.colors.text,
        fontWeight: '600',
        marginLeft: theme.spacing.md,
    },
    actionButtonRow: {
        flexDirection: 'row',
        gap: theme.spacing.md,
    },
    editButton: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme.colors.primary,
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        gap: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    editButtonText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '600',
    },
    deleteButtonAction: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#E53935',
        paddingVertical: theme.spacing.md,
        paddingHorizontal: theme.spacing.lg,
        borderRadius: theme.borderRadius.lg,
        gap: theme.spacing.sm,
        ...theme.shadows.sm,
    },
    deleteButtonText: {
        ...theme.typography.button,
        color: '#FFFFFF',
        fontWeight: '600',
    },
    deleteButton: {
        justifyContent: 'center',
        alignItems: 'center',
        paddingLeft: theme.spacing.sm,
    },
    bottomPadding: {
        height: theme.spacing.xl,
    },
    inactiveMessage: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: theme.colors.background,
        paddingHorizontal: theme.spacing.sm,
        paddingVertical: theme.spacing.xs,
        borderRadius: theme.borderRadius.md,
    },
    inactiveText: {
        ...theme.typography.caption,
        color: theme.colors.textLight,
        marginLeft: theme.spacing.xs,
        fontWeight: '600',
    },
});

export default CowDetailScreen;
